import React, { useEffect, useState } from "react";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import useFetchAllData from "Hooks/useFetchAllData";
import { GetAllEmployeesForSetup, GetAllSalaryTypes } from "adminApi/SalarySetupApi/SalarySetup";

const EditSalarySetupForm = ({
    values = {},
    handleChange = () => { },
    setValues = () => { },
    handleSubmit = () => { },
    loading = false,
    errors = {},
    title = "Edit Salary Setup"
}) => {
    const { data: employees } = useFetchAllData(GetAllEmployeesForSetup);
    const { data: salaryTypes } = useFetchAllData(GetAllSalaryTypes);

    const [allowances, setAllowances] = useState([]);
    const [deductions, setDeductions] = useState([]);
    const [grossSalary, setGrossSalary] = useState(0);

    // Logic for syncing data remains unchanged
    useEffect(() => {
        if (!salaryTypes || !values.salary_data?.length) return;

        setAllowances(salaryTypes.filter(t => t.salary_type === "Allowance"));
        setDeductions(salaryTypes.filter(t => t.salary_type === "Deduction"));

        const missingTypes = salaryTypes.filter(st =>
            !values.salary_data.some(vd => vd.salary_type_id === st.salary_type_id)
        );

        if (missingTypes.length > 0) {
            const basicItem = values.salary_data.find(v => v.sal_name === "Basic");
            const basicAmount = Number(basicItem?.amount || 0);

            const merged = [
                ...values.salary_data,
                ...missingTypes.map(t => {
                    const defaultPercent = Number(t.default_amount || 0);
                    const amount = (defaultPercent / 100) * basicAmount;
                    return {
                        salary_type_id: t.salary_type_id,
                        sal_name: t.sal_name,
                        salary_type: t.salary_type,
                        percent: defaultPercent,
                        amount: amount.toFixed(2),
                    };
                }),
            ];
            setValues(prev => ({ ...prev, salary_data: merged }));
        }
    }, [salaryTypes, values.employee_id]);

    useEffect(() => {
        if (!values.salary_data?.length) return;

        const totalAllowance = values.salary_data
            .filter(item => item.salary_type === "Allowance")
            .reduce((sum, item) => sum + Number(item.amount || 0), 0);

        const totalDeduction = values.salary_data
            .filter(item => item.salary_type === "Deduction")
            .reduce((sum, item) => sum + Number(item.amount || 0), 0);

        setGrossSalary(totalAllowance - totalDeduction);
    }, [values.salary_data]);

    const handleValueChange = (typeId, field, val) => {
        if (!values.salary_data?.length) return;

        const basicItem = values.salary_data.find(item => item.sal_name === "Basic");
        const basicAmount = Number(basicItem?.amount || 0);
        const isBasicField = values.salary_data.find(i => i.salary_type_id === typeId)?.sal_name === "Basic";

        if (isBasicField) {
            const newBasic = Number(val);
            const updatedData = values.salary_data.map(item => {
                if (item.sal_name === "Basic") return { ...item, amount: val };
                const newAmount = (Number(item.percent || 0) / 100) * newBasic;
                return { ...item, amount: newAmount.toFixed(2) };
            });
            setValues(prev => ({ ...prev, salary_data: updatedData }));
        } else {
            const updatedData = values.salary_data.map(item => {
                if (item.salary_type_id === typeId) {
                    const percentage = Number(val);
                    const newAmount = (percentage / 100) * basicAmount;
                    return { ...item, percent: val, amount: newAmount.toFixed(2) };
                }
                return item;
            });
            setValues(prev => ({ ...prev, salary_data: updatedData }));
        }
    };

    const selectedEmp = (employees || []).find(e => Number(e.employee_id) === Number(values.employee_id));

    const renderTable = (data, title, typeClass) => (
        <div className="card border-0 shadow-sm mb-4" >
            <div className={`card-header  py-3 border-bottom-0`}>
                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                    <span className={`p-1 bg-${typeClass} rounded-circle`} style={{ width: '10px', height: '10px' }}></span>
                    {title}
                </h5>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table modern-table mb-0">
                        <thead>
                            <tr>
                                <th className="ps-4 py-3 text-muted small text-uppercase fw-bold">Salary Type</th>
                                <th className="py-3 text-muted small text-uppercase fw-bold text-center" colSpan={2}>Input % / ₹</th>
                                <th className="pe-4 py-3 text-muted small text-uppercase fw-bold text-end">Totle Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(type => {
                                const item = values.salary_data?.find(v => v.salary_type_id === type.salary_type_id);
                                const isBasic = type.sal_name === "Basic";

                                return (
                                    <tr key={type.salary_type_id} className="align-middle">
                                        <td className="ps-4 py-3 fw-semibold">{type.sal_name}</td>
                                        <td className="py-2 text-center" colSpan={2} style={{ minWidth: '180px' }}>
                                            <div className="input-group input-group-md border rounded-2 overflow-hidden">
                                                <input
                                                    type="number"
                                                    className="form-control border-0 bg-light text-center fw-bold"
                                                    value={isBasic ? item?.amount ?? "" : item?.percent ?? ""}
                                                    onChange={(e) => handleValueChange(type.salary_type_id, isBasic ? "amount" : "percent", e.target.value)}
                                                    disabled={isBasic}
                                                    placeholder="0"
                                                />
                                                <span className="input-group-text bg-light border-0 text-muted fw-bold">
                                                    {isBasic ? '₹' : '%'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="pe-4 py-3 text-end">
                                            <span className="fw-bold text-indigo fs-6">
                                                ₹{Number(item?.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-light">
                                <td className="ps-4 py-3 fw-bold">Total {title}</td>
                                <td colSpan={2}></td>
                                <td className="pe-4 py-3 text-end text-primary">
                                    <h5 className="mb-0 fw-bold">
                                        ₹{data.reduce((sum, type) => {
                                            const item = values.salary_data?.find(v => v.salary_type_id === type.salary_type_id);
                                            return sum + Number(item?.amount || 0);
                                        }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </h5>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <PageBody onSubmit={handleSubmit} headerchildren={<HeadingOne>{title}</HeadingOne>}>

            {/* Employee Info Header */}
            <div className="col-12 mb-4">
                <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                    <div className="card-body p-4 ">
                        <div className="row g-4 align-items-center">
                            <div className="col-md-6 border-md-end">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold fs-4" style={{ width: '50px', height: '50px' }}>
                                        {selectedEmp?.first_name?.charAt(0) || 'E'}
                                    </div>
                                    <div>
                                        <h5 className="mb-0 fw-bold">{selectedEmp ? `${selectedEmp.first_name} ${selectedEmp.last_name}` : 'Loading...'}</h5>
                                        <span className="text-muted small">Employee ID: #{values.employee_id}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <span className="text-muted small text-uppercase fw-bold d-block mb-1">Pay Structure</span>
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 fw-bold text-primary">{selectedEmp?.pay_frequency || 'N/A'}</h5>
                                    <i className="bi bi-info-circle text-muted"></i>
                                </div>
                                <span className="text-muted small">Based on system default salary types</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Layout */}
            <div className="col-12 col-xl-6">
                {renderTable(allowances, "Allowances", "success")}
            </div>
            <div className="col-12 col-xl-6">
                {renderTable(deductions, "Deductions", "danger")}
            </div>


            {/* Gross Salary Summary Bar */}
            <div className="col-12 mb-4">
                <div className="card border-0 shadow-lg bg-dark text-white rounded-3 overflow-hidden">
                    <div className="card-body p-4">
                        <span className="text-white-50 text-uppercase small fw-bold d-block mb-2">Estimated Gross Salary</span>
                        <div className="d-flex align-items-baseline gap-2">
                            <h1 className="mb-0 fw-bold display-4">
                                ₹{grossSalary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </h1>
                            <span className="text-white-50 fs-5">/ Month</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12 text-end">
                <FormSubmitButton
                    label="Update"
                    loading={loading}
                    disabled={!values.employee_id || grossSalary <= 0}
                    colSize="auto"
                    customClass="btn btn-primary px-5 py-3 fw-bold text-uppercase fs-6 shadow-sm"
                />
            </div>


        </PageBody>
    );
};

export default EditSalarySetupForm;
